import PageLayout from "@/components/layout/PageLayout";
import { getHyperliquidMaxBuilderFeesQueryKey } from "@nitro-ape/shared/features/hyperliquid/api/get-hyperliquid-max-builder-fees";
import { getHyperliquidReferralInfoQueryKey } from "@nitro-ape/shared/features/hyperliquid/api/get-hyperliquid-referral-info";
import PerpsActiveAssetDataListener from "@nitro-ape/shared/features/hyperliquid/api/listeners/active-asset-data-listener";
import PerpsAssetCtxsListener from "@nitro-ape/shared/features/hyperliquid/api/listeners/asset-ctxs-listener";
import HyperliquidTradeHistoryListener from "@nitro-ape/shared/features/hyperliquid/api/listeners/trade-history-listener";
import HyperliquidUserDataListener from "@nitro-ape/shared/features/hyperliquid/api/listeners/user-data-listener";
import { useHyperliquidWallet } from "@nitro-ape/shared/features/wallets/api/get-wallets";
import { ChartOptionsProvider } from "@/lib/tradingview/chart-options-context";
import { Chain } from "@nitro-ape/shared/models/enums/chain";
import { Spinner } from "@heroui/spinner";
import { translateBrand } from "@/lib/i18n";
import { BrandMessageIds } from "@nitro-ape/shared/lib/brand-config";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { Suspense, useEffect, useRef } from "react";
import { PoolIdProvider } from "@nitro-ape/shared/providers/pool-id-provider";
import PerpetualsSidebar from "../../../components/trading/Sidebar/PerpetualsSidebar";
import RecentPerpListener from "./-components/listeners/RecentPerpListener";
import { MarketCloseModalProvider } from "../../../components/trading/modals/providers/market-close-modal-provider";
import { PositionMarginModalProvider } from "./-providers/position-margin-modal-provider";
import { PositionTPSLModalProvider } from "./-providers/position-tpsl-modal-provider";
import TradingResizableSection from "@/components/trading/TradingResizableSection";
import { LimitCloseModalProvider } from "@/components/trading/modals/providers/limit-close-modal-provider";
import { HyperliquidCoinProvider } from "@nitro-ape/shared/features/hyperliquid/api/providers/hyperliquid-coin-provider";

export const Route = createFileRoute("/_main/perpetuals/$coin")({
  head: () => ({
    meta: [
      {
        title: translateBrand(BrandMessageIds.metaTitlePerpetuals),
      },
    ],
  }),
  component: PerpetualsCoinPage,
});

function PerpetualsCoinPage() {
  const params = useParams({ from: "/_main/perpetuals/$coin" });
  const coin = params.coin as string;
  const hyperliquidWallet = useHyperliquidWallet();
  const queryClient = useQueryClient();
  const prefetched = useRef(false);

  useEffect(() => {
    if (hyperliquidWallet && !prefetched.current) {
      prefetched.current = true;
      queryClient.prefetchQuery({
        queryKey: getHyperliquidReferralInfoQueryKey(hyperliquidWallet.address),
      });

      queryClient.prefetchQuery({
        queryKey: getHyperliquidMaxBuilderFeesQueryKey(
          hyperliquidWallet.address,
        ),
      });
    }
  }, [hyperliquidWallet, queryClient]);

  return (
    <PageLayout>
      <Suspense
        fallback={
          <div className="flex h-screen items-center justify-center">
            <Spinner size="lg" color="secondary" />
          </div>
        }
      >
        <ChartOptionsProvider>
          <HyperliquidCoinProvider coin={coin}>
            <HyperliquidUserDataListener>
              <HyperliquidTradeHistoryListener>
                <PerpsActiveAssetDataListener>
                  <PerpsAssetCtxsListener>
                    <RecentPerpListener coin={coin}>
                      <PoolIdProvider poolId="" chain={Chain.solana}>
                        <PositionTPSLModalProvider>
                          <PositionMarginModalProvider>
                            <MarketCloseModalProvider>
                              <LimitCloseModalProvider>
                                <div className="flex h-full max-h-full w-full gap-1.5">
                                  <TradingResizableSection />
                                  <PerpetualsSidebar />
                                </div>
                              </LimitCloseModalProvider>
                            </MarketCloseModalProvider>
                          </PositionMarginModalProvider>
                        </PositionTPSLModalProvider>
                      </PoolIdProvider>
                    </RecentPerpListener>
                  </PerpsAssetCtxsListener>
                </PerpsActiveAssetDataListener>
              </HyperliquidTradeHistoryListener>
            </HyperliquidUserDataListener>
          </HyperliquidCoinProvider>
        </ChartOptionsProvider>
      </Suspense>
    </PageLayout>
  );
}
